// services/live/userChannel — the ONE private Realtime channel a tab holds for
// the signed-in person: topic `user:<myId>` (LANE1_PLAN.md §1.2, §5, §7).
//
// What travels on it are HINTS published by database triggers (migration 0076
// onward): small, absolute, versioned events such as `unread_state`. Nothing
// here is trusted to arrive — broadcast is fire-and-forget — so the channel's
// second job is to say WHEN a consumer must re-read the truth: on every
// (re)join. That signal is `onUserChannelJoin`.
//
// Rules this module keeps:
//   • one channel per tab, ref-counted — three surfaces asking for it share it;
//   • `private: true` — the join is authorised once by the realtime.messages
//     policy (`user:<id>` = app_uid()), never per message;
//   • `realtime.setAuth()` before subscribing, or a private join is refused;
//   • recovery uses the shared jittered policy (`channelResync`) so an office
//     full of tabs does not re-read in step after a Realtime drop;
//   • a join that is REFUSED (policy not there yet, Realtime authorization off)
//     must not hammer the join limiter: after a few refusals the channel is
//     dropped and tried again much later. Consumers keep working from re-reads.
//   • clients never publish (there is no insert policy); this module has no send.
import { supabase, channelResync } from '../supabaseClient';

type Listener = (payload: any) => void;

/** Refusals in a row, before ever joining, that make this tab stop trying for a while. */
export const USER_CHANNEL_MAX_REFUSALS = 3;
/** How long a refused tab waits before one more attempt (jittered ±20%). */
export const USER_CHANNEL_RETRY_MS = 10 * 60 * 1000;

const listeners = new Map<string, Set<Listener>>();
const joinListeners = new Set<() => void>();
const unavailableListeners = new Set<() => void>();

let userId: string | null = null;
/** Bumped when the account changes, so a release from the old account is ignored. */
let session = 0;
let refs = 0;
let channel: any = null;
let joined = false;
let refusals = 0;
/** setAuth() is in flight — a second holder must not start a second open. */
let opening = false;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
/** Bumped on every open/close so a late async step of an old attempt is a no-op. */
let generation = 0;

function signalJoin(): void {
  for (const fn of [...joinListeners]) {
    try { fn(); } catch { /* a consumer's failure is not the channel's */ }
  }
}

function dispatch(event: string, payload: unknown): void {
  const set = listeners.get(event);
  if (!set) return;
  for (const fn of [...set]) {
    try { fn(payload); } catch { /* same */ }
  }
}

function close(): void {
  generation += 1;
  joined = false;
  if (retryTimer) { clearTimeout(retryTimer); retryTimer = null; }
  const ch = channel;
  channel = null;
  if (ch) {
    try { supabase().removeChannel(ch); } catch { /* already gone */ }
  }
}

async function open(): Promise<void> {
  if (!userId || channel || opening) return;
  const gen = ++generation;
  const sb = supabase();
  opening = true;
  try {
    // A private channel is authorised with the socket's token; make sure it is
    // the current one. Failure here is not fatal — subscribe() reports it.
    await sb.realtime?.setAuth?.();
  } catch { /* fall through */ } finally {
    opening = false;
  }
  if (!userId || channel) return;
  if (gen !== generation) {
    // Closed (or the account changed) while setAuth was in flight. If somebody
    // still holds the channel, start over for whoever that is now.
    if (refs > 0) void open();
    return;
  }

  // Re-join after a drop → one jittered "re-read the truth"; still down → one
  // late one. The FIRST join is signalled directly below (channelResync
  // deliberately stays quiet on it).
  const track = channelResync(() => { if (gen === generation) signalJoin(); });
  let everJoined = false;

  const ch = sb.channel(`user:${userId}`, { config: { private: true } });
  ch.on('broadcast', { event: '*' }, (msg: any) => {
    if (gen !== generation) return;
    if (msg && typeof msg.event === 'string') dispatch(msg.event, msg.payload);
  });
  channel = ch;
  ch.subscribe((status: string) => {
    if (gen !== generation) return;
    track(status);
    if (status === 'SUBSCRIBED') {
      refusals = 0;
      joined = true;
      if (!everJoined) {
        everJoined = true;
        // Hints published between a consumer's first truth read and this join
        // were never delivered to us — say so now.
        signalJoin();
      }
      return;
    }
    if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      joined = false;
    }
    if ((status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') && !everJoined) {
      refusals += 1;
      if (refusals >= USER_CHANNEL_MAX_REFUSALS) {
        // Never got in. Stop the library's own rejoin loop and come back later.
        close();
        refusals = 0;
        const wait = USER_CHANNEL_RETRY_MS * (0.8 + Math.random() * 0.4);
        retryTimer = setTimeout(() => { retryTimer = null; if (refs > 0) void open(); }, wait);
        for (const fn of [...unavailableListeners]) {
          try { fn(); } catch { /* a consumer's failure is not the channel's */ }
        }
      }
    }
  });
}

/**
 * Hold the `user:<id>` channel. Ref-counted: the first holder opens it, the
 * last release closes it. A different user id (account switch) replaces it.
 */
export function joinUserChannel(id: string): () => void {
  if (userId !== id) {
    close();
    userId = id;
    session += 1;
    refusals = 0;
    refs = 0;
  }
  const mine = session;
  refs += 1;
  if (!channel && !retryTimer) void open();
  let released = false;
  return () => {
    if (released) return;
    released = true;
    if (session !== mine) return; // already replaced by another account
    refs -= 1;
    if (refs <= 0) {
      refs = 0;
      userId = null;
      close();
    }
  };
}

/** Listen for one event name on the user channel (e.g. `unread_state`). */
export function onUserEvent(event: string, cb: Listener): () => void {
  let set = listeners.get(event);
  if (!set) { set = new Set(); listeners.set(event, set); }
  set.add(cb);
  return () => {
    const s = listeners.get(event);
    if (!s) return;
    s.delete(cb);
    if (s.size === 0) listeners.delete(event);
  };
}

/** "Re-read the truth now": fired on the first join and (jittered) on every re-join. */
export function onUserChannelJoin(cb: () => void): () => void {
  joinListeners.add(cb);
  return () => { joinListeners.delete(cb); };
}

/** True while hints can reach this tab. */
export function isUserChannelJoined(): boolean {
  return joined;
}

/**
 * The channel was REFUSED (policy not there, Realtime authorization off) and
 * this tab has given up for a while: a consumer with an old path falls back.
 */
export function onUserChannelUnavailable(cb: () => void): () => void {
  unavailableListeners.add(cb);
  return () => { unavailableListeners.delete(cb); };
}

/** Test seam — drops every piece of module state. */
export function _resetUserChannel(): void {
  close();
  listeners.clear();
  joinListeners.clear();
  unavailableListeners.clear();
  userId = null;
  session += 1;
  refs = 0;
  refusals = 0;
}
